import { createApi } from '@reduxjs/toolkit/query/react';

import { authQuery } from './baseQuery';
import type { Languages, PaginatedResponse, TableDataResponse } from 'src/types/common';
import type {
  IWebhookEvent,
  EventTypeEnum,
  IWebhookSubscription,
  WebhooksHistoryStatuses,
} from 'src/types/webhooks';
import { withUtcIsoDateRangeState } from 'src/utils/date-picker';
import type { IPaginationState } from 'src/utils/server-side-pagination';
import { convertObjToUrl } from 'src/utils/server-side-pagination';
import { ROWS_PER_PAGE } from 'src/pages/webhook-subscriptions/useGetPaginatedSubscriptions';

export const webhooksAPISlice = createApi({
  reducerPath: 'presentation-service-webhooks-api',
  tagTypes: ['Subscriptions', 'WebhookHistory'],
  baseQuery: (args: any, api: any, extraOptions: any) =>
    authQuery(
      args,
      api,
      (extraOptions = { ...extraOptions, baseUrl: window.RUNTIME_CONFIG.PRESENTATION_SERVICE })
    ),
  endpoints: (builder) => ({
    getAllSubscriptions: builder.query<TableDataResponse<IWebhookSubscription>, void>({
      query: () => '/subscriptions',
      providesTags: ['Subscriptions'],
    }),

    getSubscriptions: builder.query<
      PaginatedResponse<IWebhookSubscription>,
      { state: IPaginationState; limit: number }
    >({
      query: ({ state, limit }) => `/subscriptions?limit=${limit}${convertObjToUrl(state)}`,
      providesTags: ['Subscriptions'],
    }),

    getSubscriptionById: builder.query<IWebhookSubscription, string>({
      query: (id: string) => `/subscriptions/${id}`,
      providesTags: ['Subscriptions'],
    }),

    createSubscription: builder.mutation({
      query: (body) => ({
        url: '/subscriptions',
        method: 'POST',
        body,
      }),
      // invalidatesTags: ['Subscriptions'],
      async onQueryStarted(requestData, { dispatch, queryFulfilled }) {
        try {
          const queryResponse = await queryFulfilled;

          dispatch(
            webhooksAPISlice.util.updateQueryData(
              'getSubscriptions',
              { state: { filters: {}, sorting: {}, page: 1 }, limit: 10 },
              (allSubscriptionsDraft) => {
                const newItem: IWebhookSubscription = {
                  ...queryResponse.data,
                };

                if (allSubscriptionsDraft.data.length > 0) {
                  allSubscriptionsDraft.data.unshift(newItem);
                } else {
                  allSubscriptionsDraft.data = [newItem];
                }
              }
            )
          );
        } catch {
          console.log('Error Creating Subscription!');
        }
      },
    }),

    editSubscription: builder.mutation({
      query: ({ currentRtkQueryArgs, ...body }) => ({
        url: `/subscriptions/${body.id}`,
        method: 'PATCH',
        body,
      }),
      async onQueryStarted({ currentRtkQueryArgs }, { dispatch, queryFulfilled }) {
        try {
          const { data: updatedSubscription } = await queryFulfilled;

          dispatch(
            webhooksAPISlice.util.updateQueryData(
              'getSubscriptions',
              { state: currentRtkQueryArgs, limit: ROWS_PER_PAGE },
              (draft) => {
                draft.data = draft.data.map((e) => {
                  if (e.id === updatedSubscription.id) {
                    return updatedSubscription;
                  }

                  return e;
                });
              }
            )
          );

          dispatch(
            webhooksAPISlice.util.upsertQueryData(
              'getSubscriptionById',
              updatedSubscription.id,
              updatedSubscription
            )
          );
        } catch {
          console.log('Error wihle updating');
        }
      },
    }),

    getWebhooksHistories: builder.query<
      PaginatedResponse<IWebhookEvent>,
      { state: IPaginationState; limit: number }
    >({
      query: ({ state, limit }) =>
        `/webhooks?limit=${limit}${convertObjToUrl(withUtcIsoDateRangeState(state))}`,
    }),

    getWebhookStatuses: builder.query<WebhooksHistoryStatuses, void>({
      query: () => '/webhooks/fetch-event-statuses',
    }),

    getWebhookEventById: builder.query<
      IWebhookEvent,
      {
        id: string;
        type: string;
      }
    >({
      query: ({ id, type }) => `/webhooks/${id}?type=${type}`,
      providesTags: ['WebhookHistory'],
    }),

    retryWebhook: builder.mutation({
      query: (body) => ({
        url: `/webhooks/retry`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['WebhookHistory'],
    }),

    deleteSubscription: builder.mutation({
      query: (id) => ({
        url: `/subscriptions/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Subscriptions'],
    }),

    getAvailableEvents: builder.query<
      {
        key: EventTypeEnum;
        value: string;
      }[],
      {
        language: Languages;
      }
    >({
      query: ({ language }) => `/webhooks/event-types?language=${language}`,
    }),
  }),
});

export const {
  useGetAllSubscriptionsQuery,
  useGetSubscriptionByIdQuery,
  useCreateSubscriptionMutation,
  useEditSubscriptionMutation,
  useGetWebhooksHistoriesQuery,
  useGetWebhookEventByIdQuery,
  useRetryWebhookMutation,
  useDeleteSubscriptionMutation,
  useGetAvailableEventsQuery,
  useGetSubscriptionsQuery,
  useGetWebhookStatusesQuery,
} = webhooksAPISlice;
